import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  applyAccountBulkAlertAction,
  applyOrganizationBulkAlertAction,
  getAccountBulkAlertEligibility,
  getOrganizationBulkAlertEligibility,
} from '@kirin/api-client/api/alerts';
import type {
  BulkAlertAction,
  BulkAlertApplyResponse,
  BulkAlertEligibilityResponse,
  BulkAlertSelection,
} from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import {
  invalidateBulkAffectedQueries,
  type AlertSuppressionScope,
} from './alertSuppressionCache';

/**
 * Scope a bulk action targets: an organization (requires manager/owner authz)
 * or the authenticated user's own alerts (self-scoped). Reuses the cache-layer
 * scope so eligibility keys + invalidation stay aligned across both surfaces.
 */
export type BulkAlertScope = AlertSuppressionScope;

function bulkEligibilityQueryKey(
  scope: BulkAlertScope,
  selection: BulkAlertSelection
): readonly unknown[] {
  return scope.kind === 'organization'
    ? queryKeys.organizations.bulkAlertEligibility(
        scope.organizationId,
        selection
      )
    : queryKeys.account.bulkAlertEligibility(selection);
}

/**
 * Hooks for the Alerts-screen bulk actions (KIR-3252).
 *
 * `useBulkAlertEligibility` drives the pre-confirm preview (the bulk bar labels
 * + modal counts). `useBulkAlertAction` applies an all-or-nothing bulk action
 * and, on settle, invalidates the alerts list / occurrences / USP / dashboard
 * widgets / the eligibility preview so the bar re-derives from the new states.
 *
 * Both are pure data-layer helpers — NOT flag-gated and NOT toasting. Gating is
 * at the call site (the routes 404 when the flag is off); the success/failure
 * toast is owned by the bulk bar so the wording stays alongside the UI.
 */

/** mute/unmute touch the mute fragment; exclude/remove touch the exclude fragment. */
function fragmentForAction(action: BulkAlertAction): 'mute' | 'exclude' {
  return action === 'mute' || action === 'unmute' ? 'mute' : 'exclude';
}

/**
 * Eligibility preview for a selection. Keyed by `(organizationId, selection)`
 * so distinct selections cache separately; `placeholderData` keeps the prior
 * counts visible while a new selection's request is in flight (no flicker to
 * empty). Caller gates `enabled` (selection non-empty + a relevant flag on).
 */
export function useBulkAlertEligibility(
  scope: BulkAlertScope,
  selection: BulkAlertSelection,
  options?: { enabled?: boolean }
): UseQueryResult<BulkAlertEligibilityResponse, Error> {
  return useQuery({
    queryKey: bulkEligibilityQueryKey(scope, selection),
    queryFn: () =>
      scope.kind === 'organization'
        ? getOrganizationBulkAlertEligibility(apiClient, scope.organizationId, {
            selection,
          })
        : getAccountBulkAlertEligibility(apiClient, { selection }),
    enabled: options?.enabled ?? true,
    placeholderData: previous => previous,
    staleTime: 0,
  });
}

export interface BulkAlertActionVariables {
  action: BulkAlertAction;
  selection: BulkAlertSelection;
  /** Consumed only for `mute`. ISO datetime or null (forever). */
  mutedUntil?: string | null;
  /** Consumed only for `exclude`. ISO datetime or null (forever). */
  excludedUntil?: string | null;
}

/**
 * Apply a bulk action. All-or-nothing → resolves to `{ action, changedCount }`;
 * there is no optimistic patch or undo (the backend write is atomic and the
 * caller re-queries eligibility on settle). The caller wires the
 * success/failure toast off this mutation.
 */
export function useBulkAlertAction(
  scope: BulkAlertScope
): UseMutationResult<BulkAlertApplyResponse, Error, BulkAlertActionVariables> {
  const queryClient = useQueryClient();

  return useMutation<BulkAlertApplyResponse, Error, BulkAlertActionVariables>({
    mutationFn: ({ action, selection, mutedUntil, excludedUntil }) =>
      scope.kind === 'organization'
        ? applyOrganizationBulkAlertAction(
            apiClient,
            scope.organizationId,
            action,
            { selection, mutedUntil, excludedUntil }
          )
        : applyAccountBulkAlertAction(apiClient, action, {
            selection,
            mutedUntil,
            excludedUntil,
          }),
    onSettled: (_data, _error, variables) => {
      invalidateBulkAffectedQueries(
        queryClient,
        scope,
        fragmentForAction(variables.action)
      );
    },
  });
}
